import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext'; // This path is correct for standard setups
import { toast } from 'react-toastify'; // For notifications
import { Draw, PaymentRequest } from '../../types'; // Assuming these types are defined

// Define a type for the dashboard statistics
interface DashboardStats {
    totalDraws: number;
    activeDraws: number;
    declaredDraws: number;
    totalDepositsValue: number;
    totalWithdrawalsValue: number;
    pendingDeposits: number;
    pendingWithdrawals: number;
    totalBets: number;
    activeBets: number;
    winningBets: number; // Placeholder for now, requires backend aggregation
    totalUsers: number; // New statistic
}

const AdminDashboard: React.FC = () => {
    const { user } = useAuth();
    const [stats, setStats] = useState<DashboardStats | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Custom Tailwind animations via CSS (copied for consistency)
    const customAnimations = `
        @keyframes fade-in-down {
            from { opacity: 0; transform: translateY(-20px); }
            to { opacity: 1; transform: translateY(0); }
        }
        @keyframes fade-in-up {
            from { opacity: 0; transform: translateY(20px); }
            to { opacity: 1; transform: translateY(0); }
        }
        @keyframes zoom-in-out {
            0% { transform: scale(0.9); opacity: 0; }
            50% { transform: scale(1.05); opacity: 1; }
            100% { transform: scale(1); }
        }
        @keyframes bounce-subtle {
            0%, 100% { transform: translateY(0); }
            50% { transform: translateY(-3px); }
        }
        @keyframes pulse-slow {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.7; }
        }
        .animate-fade-in-down {
            animation: fade-in-down 0.8s ease-out forwards;
        }
        .animate-fade-in-up {
            animation: fade-in-up 0.8s ease-out forwards;
        }
        .animate-zoom-in-out {
            animation: zoom-in-out 0.5s ease-out forwards;
        }
        .animate-bounce-subtle {
            animation: bounce-subtle 1.5s infinite ease-in-out;
        }
        .animate-pulse-slow {
            animation: pulse-slow 3s infinite ease-in-out;
        }
    `;


    const fetchDashboardStats = useCallback(async () => {
        setLoading(true);
        setError(null);

        if (!user || !user.token || !user.isAdmin) {
            toast.error('Authentication error: Not authorized to view this page.');
            setLoading(false);
            return;
        }

        try {
            // Fetch all draws
            const drawsResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}/admin/draws`, {
                headers: { 'Authorization': `Bearer ${user.token}` },
            });
            if (!drawsResponse.ok) throw new Error('Failed to fetch draws for statistics.');
            const draws: Draw[] = await drawsResponse.json();

            // Fetch all payment requests
            const paymentsResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}/admin/payments?status=All&type=All`, {
                headers: { 'Authorization': `Bearer ${user.token}` },
            });
            if (!paymentsResponse.ok) throw new Error('Failed to fetch payments for statistics.');
            const payments: PaymentRequest[] = await paymentsResponse.json();

            // Fetch all users for total user count
            const usersResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}/admin/users`, {
                headers: { 'Authorization': `Bearer ${user.token}` },
            });
            if (!usersResponse.ok) throw new Error('Failed to fetch users for statistics.');
            const users: any[] = await usersResponse.json(); // Type as any[] as we don't have a User type defined in frontend for all fields

            // --- Aggregation Logic (Client-side for now) ---
            const totalDraws = draws.length;
            const activeDraws = draws.filter(d => d.status === 'active').length;
            const declaredDraws = draws.filter(d => d.status === 'declared').length;

            const totalDepositsValue = payments
                .filter(p => p.type === 'deposit' && p.status === 'approved')
                .reduce((sum, p) => sum + p.amount, 0);

            const totalWithdrawalsValue = payments
                .filter(p => p.type === 'withdrawal' && p.status === 'approved')
                .reduce((sum, p) => sum + p.amount, 0);

            const pendingDeposits = payments.filter(p => p.type === 'deposit' && p.status === 'pending').length;
            const pendingWithdrawals = payments.filter(p => p.type === 'withdrawal' && p.status === 'pending').length;

            // Simplified: count locked numbers as total bets for now.
            const totalBets = draws.reduce((acc, draw) => acc + (draw.lockedNumbers?.length || 0), 0);
            const activeBets = draws.filter(d => d.status === 'active').reduce((acc, draw) => acc + (draw.lockedNumbers?.length || 0), 0);

            // winningBets: Requires a dedicated backend endpoint or a more complex client-side aggregation
            // involving fetching all bets and filtering by isWinner and drawId, which is inefficient for a dashboard.
            // For now, it's set to 0. For accurate data, consider a new backend API endpoint.
            const winningBets = 0;
            const totalUsers = users.length;

            setStats({
                totalDraws,
                activeDraws,
                declaredDraws,
                totalDepositsValue,
                totalWithdrawalsValue,
                pendingDeposits,
                pendingWithdrawals,
                totalBets,
                activeBets,
                winningBets,
                totalUsers, // New stat
            });

        } catch (err: any) {
            console.error('Error fetching dashboard stats:', err);
            setError(err.message || 'Failed to load dashboard statistics.');
            toast.error(err.message || 'Error loading dashboard statistics.');
        } finally {
            setLoading(false);
        }
    }, [user]);

    useEffect(() => {
        fetchDashboardStats();
    }, [fetchDashboardStats]);

    if (loading) {
        return (
            <div className="container mx-auto p-4 md:p-8 min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-100 to-purple-200 font-inter">
                <style>{customAnimations}</style>
                <div className="text-center">
                    <p className="text-2xl font-semibold text-purple-700 animate-pulse-slow">
                        Loading dashboard data...
                    </p>
                    <div className="mt-4 w-16 h-16 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="container mx-auto p-4 md:p-8 min-h-screen flex items-center justify-center bg-gradient-to-br from-red-100 to-orange-100 font-inter">
                <style>{customAnimations}</style>
                <div className="bg-white p-8 rounded-lg shadow-xl border-t-8 border-red-500 w-full max-w-md text-center animate-zoom-in-out">
                    <h2 className="text-3xl font-bold text-red-700 mb-4">Error!</h2>
                    <p className="text-lg text-gray-700">{error}</p>
                    <button
                        onClick={fetchDashboardStats}
                        className="mt-6 bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-md transition duration-300 shadow-md"
                    >
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    if (!user || !user.isAdmin) {
        return (
            <div className="container mx-auto p-4 md:p-8 min-h-screen flex items-center justify-center bg-gradient-to-br from-red-100 to-orange-100 font-inter">
                <style>{customAnimations}</style>
                <div className="bg-white p-8 rounded-lg shadow-xl border-t-8 border-red-500 w-full max-w-md text-center animate-zoom-in-out">
                    <h2 className="text-3xl font-bold text-red-700 mb-4">Access Denied!</h2>
                    <p className="text-lg text-gray-700">You do not have administrative privileges to view this page.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-purple-50 to-indigo-100 flex flex-col items-center justify-start py-8 px-4 sm:px-6 lg:px-8 font-inter">
            <style>{customAnimations}</style> {/* Inject custom animations */}

            <div className="bg-white rounded-xl shadow-2xl p-6 sm:p-8 lg:p-10 w-full max-w-4xl border border-gray-100 transform transition-all duration-300 hover:scale-[1.005]">
                <h1 className="text-4xl font-extrabold text-center mb-10 text-gray-900 drop-shadow-sm animate-fade-in-down">
                    Admin Dashboard Overview
                </h1>

                {/* Management Links Section */}
                <section className="mb-12">
                    <h2 className="text-3xl font-bold text-center mb-8 text-indigo-700 animate-fade-in-up">Quick Actions</h2>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-6 sm:gap-8"> {/* Changed to 4 columns */}
                        {/* Link to Create New Draw */}
                        <Link
                            to="/admin/draws/create"
                            className="bg-gradient-to-br from-blue-500 to-blue-700 text-white p-6 rounded-xl shadow-lg hover:shadow-xl transition duration-300 transform hover:scale-105 flex flex-col items-center justify-center text-center space-y-3 border border-blue-400 focus:outline-none focus:ring-4 focus:ring-blue-300 animate-zoom-in-out"
                        >
                            <span className="text-6xl mb-2">➕</span>
                            <h2 className="text-2xl font-bold">Create New Draw</h2>
                            <p className="text-blue-100 text-sm">Set up upcoming betting draws.</p>
                        </Link>

                        {/* Link to Manage Draws */}
                        <Link
                            to="/admin/draws/manage"
                            className="bg-gradient-to-br from-purple-500 to-indigo-700 text-white p-6 rounded-xl shadow-lg hover:shadow-xl transition duration-300 transform hover:scale-105 flex flex-col items-center justify-center text-center space-y-3 border border-purple-400 focus:outline-none focus:ring-4 focus:ring-purple-300 animate-zoom-in-out"
                        >
                            <span className="text-6xl mb-2">⚙️</span>
                            <h2 className="text-2xl font-bold">Manage Draws</h2>
                            <p className="text-purple-100 text-sm">Close draws & declare winners.</p>
                        </Link>

                        {/* Link to Manage Payments */}
                        <Link
                            to="/admin/payments/manage"
                            className="bg-gradient-to-br from-green-500 to-teal-700 text-white p-6 rounded-xl shadow-lg hover:shadow-xl transition duration-300 transform hover:scale-105 flex flex-col items-center justify-center text-center space-y-3 border border-green-400 focus:outline-none focus:ring-4 focus:ring-green-300 animate-zoom-in-out"
                        >
                            <span className="text-6xl mb-2">💰</span>
                            <h2 className="text-2xl font-bold">Manage Payments</h2>
                            <p className="text-green-100 text-sm">Approve user payment proofs.</p>
                        </Link>

                        {/* NEW: Link to Manage Users */}
                        <Link
                            to="/admin/users/manage"
                            className="bg-gradient-to-br from-red-500 to-pink-700 text-white p-6 rounded-xl shadow-lg hover:shadow-xl transition duration-300 transform hover:scale-105 flex flex-col items-center justify-center text-center space-y-3 border border-red-400 focus:outline-none focus:ring-4 focus:ring-red-300 animate-zoom-in-out"
                        >
                            <span className="text-6xl mb-2">👥</span>
                            <h2 className="text-2xl font-bold">Manage Users</h2>
                            <p className="text-red-100 text-sm">View & manage all registered users.</p>
                        </Link>
                    </div>
                </section>

                {/* Statistics Section */}
                {stats && (
                    <section>
                        <h2 className="text-3xl font-bold text-center mb-8 text-indigo-700 animate-fade-in-up">At a Glance</h2>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                            {/* Total Draws */}
                            <div className="bg-blue-50 p-6 rounded-xl shadow-md border border-blue-200 flex flex-col items-center text-center animate-fade-in-up">
                                <span className="text-5xl text-blue-600 mb-3">🔢</span>
                                <p className="text-lg text-gray-700 font-semibold">Total Draws</p>
                                <p className="text-4xl font-extrabold text-blue-800">{stats.totalDraws}</p>
                            </div>

                            {/* Active Draws */}
                            <div className="bg-green-50 p-6 rounded-xl shadow-md border border-green-200 flex flex-col items-center text-center animate-fade-in-up delay-100">
                                <span className="text-5xl text-green-600 mb-3">✅</span>
                                <p className="text-lg text-gray-700 font-semibold">Active Draws</p>
                                <p className="text-4xl font-extrabold text-green-800">{stats.activeDraws}</p>
                            </div>

                            {/* Declared Draws */}
                            <div className="bg-purple-50 p-6 rounded-xl shadow-md border border-purple-200 flex flex-col items-center text-center animate-fade-in-up delay-200">
                                <span className="text-5xl text-purple-600 mb-3">🏆</span>
                                <p className="text-lg text-gray-700 font-semibold">Declared Draws</p>
                                <p className="text-4xl font-extrabold text-purple-800">{stats.declaredDraws}</p>
                            </div>

                            {/* Total Deposits Value */}
                            <div className="bg-teal-50 p-6 rounded-xl shadow-md border border-teal-200 flex flex-col items-center text-center animate-fade-in-up delay-300">
                                <span className="text-5xl text-teal-600 mb-3">💸</span>
                                <p className="text-lg text-gray-700 font-semibold">Total Deposits</p>
                                <p className="text-4xl font-extrabold text-teal-800">Rs. {stats.totalDepositsValue.toFixed(2)}</p>
                            </div>

                            {/* Total Withdrawals Value */}
                            <div className="bg-red-50 p-6 rounded-xl shadow-md border border-red-200 flex flex-col items-center text-center animate-fade-in-up delay-400">
                                <span className="text-5xl text-red-600 mb-3"> withdrawals </span>
                                <p className="text-lg text-gray-700 font-semibold">Total Withdrawals</p>
                                <p className="text-4xl font-extrabold text-red-800">Rs. {stats.totalWithdrawalsValue.toFixed(2)}</p>
                            </div>

                            {/* Pending Deposits */}
                            <div className="bg-yellow-50 p-6 rounded-xl shadow-md border border-yellow-200 flex flex-col items-center text-center animate-fade-in-up delay-500">
                                <span className="text-5xl text-yellow-600 mb-3">⏳</span>
                                <p className="text-lg text-gray-700 font-semibold">Pending Deposits</p>
                                <p className="text-4xl font-extrabold text-yellow-800">{stats.pendingDeposits}</p>
                            </div>

                            {/* Pending Withdrawals */}
                            <div className="bg-orange-50 p-6 rounded-xl shadow-md border border-orange-200 flex flex-col items-center text-center animate-fade-in-up delay-600">
                                <span className="text-5xl text-orange-600 mb-3">⌛</span>
                                <p className="text-lg text-gray-700 font-semibold">Pending Withdrawals</p>
                                <p className="text-4xl font-extrabold text-orange-800">{stats.pendingWithdrawals}</p>
                            </div>

                             {/* Total Bets */}
                             <div className="bg-indigo-50 p-6 rounded-xl shadow-md border border-indigo-200 flex flex-col items-center text-center animate-fade-in-up delay-700">
                                <span className="text-5xl text-indigo-600 mb-3">🎰</span>
                                <p className="text-lg text-gray-700 font-semibold">Total Bets Placed</p>
                                <p className="text-4xl font-extrabold text-indigo-800">{stats.totalBets}</p>
                            </div>

                            {/* Active Bets */}
                            <div className="bg-blue-50 p-6 rounded-xl shadow-md border border-blue-200 flex flex-col items-center text-center animate-fade-in-up delay-800">
                                <span className="text-5xl text-blue-600 mb-3">🔥</span>
                                <p className="text-lg text-gray-700 font-semibold">Active Bets</p>
                                <p className="text-4xl font-extrabold text-blue-800">{stats.activeBets}</p>
                            </div>

                             {/* Winning Bets */}
                             <div className="bg-green-50 p-6 rounded-xl shadow-md border border-green-200 flex flex-col items-center text-center animate-fade-in-up delay-900">
                                <span className="text-5xl text-green-600 mb-3">🎉</span>
                                <p className="text-lg text-gray-700 font-semibold">Winning Bets</p>
                                <p className="text-4xl font-extrabold text-green-800">{stats.winningBets}</p>
                            </div>

                            {/* NEW: Total Users */}
                            <div className="bg-pink-50 p-6 rounded-xl shadow-md border border-pink-200 flex flex-col items-center text-center animate-fade-in-up delay-1000">
                                <span className="text-5xl text-pink-600 mb-3">🧑‍💻</span>
                                <p className="text-lg text-gray-700 font-semibold">Total Users</p>
                                <p className="text-4xl font-extrabold text-pink-800">{stats.totalUsers}</p>
                            </div>

                        </div>
                    </section>
                )}
            </div>
        </div>
    );
};

export default AdminDashboard;
